import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { calendrierAPI, evenementsAPI, departementsAPI, dashboardAPI, planningAPI } from '../services/api';
import { departementCourant, niveauModule, NIVEAU_CONSULTER } from '../services/droits';
import { CATEGORIES_AMORCAGE, CODE_FILET, iconeEvenement, couleurEvenement } from '../services/evenementsCategories';
import './PlanningApp.css';
import ModalePlanning from './ModalePlanning';
import PlanningTopbar from './PlanningTopbar';
import Calendar from './Calendar';
import Legend from './Legend';
import DashboardCompact from "./DashboardCompact";
import FenetreDetachee, { reprendreFenetre } from './FenetreDetachee';
import EventManager from './EventManager';
import PlanningAVerifier from './PlanningAVerifier';

/* ═══ LES FENÊTRES DÉTACHÉES VIVENT HORS DU COMPOSANT (lot 5.5 bis) ═══════════
   Registre au niveau du module, et non dans un `useRef` : une fenêtre doit
   survivre à un remontage de l'écran (React.StrictMode en développement, ou
   n'importe quel ancêtre qui recrée son sous-arbre). Rangée dans le composant,
   elle serait perdue de vue par la nouvelle instance — l'écran afficherait
   « non détaché » pendant qu'une fenêtre orpheline reste au milieu du bureau.
   Il n'y a jamais qu'un Planning affiché à la fois : un registre unique suffit.

   `sessionStorage` complète le dispositif pour les rechargements, où même le
   module est rechargé : il se souvient de CE QUI ÉTAIT détaché, et
   `reprendreFenetre` rebranche les fenêtres restées ouvertes. */
const fenetresDetachees = { dashboard: null, affichage: null };
const CLE_DETACHES = 'planning_blocs_detaches';

const vivante = (f) => !!f && !f.closed;

const memoriserDetaches = () => {
    try {
        const ouverts = Object.keys(fenetresDetachees).filter(c => vivante(fenetresDetachees[c]));
        sessionStorage.setItem(CLE_DETACHES, JSON.stringify(ouverts));
    } catch { /* stockage indisponible : on perd la reprise, pas le module */ }
};

// Fermeture différée : un démontage suivi d'un remontage immédiat (StrictMode)
// ne doit pas emporter les fenêtres. Le montage suivant annule l'ordre.
let fermetureDifferee = null;

// onBackToAdmin / onBackToAccueil retirés au lot 5.2 : la sidebar porte les entrées
// « Administration » (visible aux administrateurs) et « Accueil ». Les deux boutons
// qui les dupliquaient dans la barre du Planning ont disparu — signature ET point
// d'appel nettoyés dans le même commit, sinon Babel garde un paramètre fantôme.
//
// Lot 5.4 : `onBackToSetup` disparaît de la même manière. Changer d'onglet n'est plus
// une navigation qui jette la configuration, c'est `onChangeOnglet` porté par
// `PlanningModule`, qui affiche l'un ou l'autre écran.
function PlanningApp({ token, user, config, onLogout, onglet, onChangeOnglet, anneeId, onAnneeChange }) {
    const [calendrier, setCalendrier] = useState(null);
    const [anneeScolaire, setAnneeScolaire] = useState(null);
    const [annees, setAnnees] = useState([]);
    const [planningAnneeId, setPlanningAnneeId] = useState(anneeId || config?.anneeScolaireId || null);
    const [loading, setLoading] = useState(true);
    const [showEvents, setShowEvents] = useState(false);
    const [placedEvents, setPlacedEvents] = useState([]);
    // Le référentiel du département (lot 5.4 ter) — chargé une fois, passé aux trois
    // écrans qui affichent des événements.
    const [categoriesEvenements, setCategoriesEvenements] = useState([]);
    const [highlightEventId, setHighlightEventId] = useState(null);
    const [dateClickInfo, setDateClickInfo] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [visibilite, setVisibilite] = useState({});
    const [visibiliteMois, setVisibiliteMois] = useState({});
    // Lot 5.1 : un enregistrement refusé ne doit plus disparaître dans un console.error
    const [visibiliteErreur, setVisibiliteErreur] = useState(null);
    const [cellStates, setCellStates] = useState({});
    // Lot 5.5 : DEUX cartes repliables INDÉPENDAMMENT, comme la maquette. Un seul
    // bouton repliait les deux à la fois — or on veut couramment le tableau de bord
    // rangé et les filtres ouverts, ou l'inverse. Les deux naissent repliées
    // (`display:none` dans le dessin) : la zone haute ne doit pas repousser le
    // calendrier hors de l'écran à chaque ouverture.
    const [dashboardCollapsed, setDashboardCollapsed] = useState(true);
    const [affichageCollapsed, setAffichageCollapsed] = useState(true);
    const [moisDetailOpen, setMoisDetailOpen] = useState(false);
    const [moisDragging, setMoisDragging] = useState(false);
    const [moisDragAction, setMoisDragAction] = useState(null);
    const calendarRef = useRef(null);
    const [dashboardData, setDashboardData] = useState([]);
    // Lot 5.5 bis — détachement sur un second écran (spec §6, écart E18 refermé).
    // Chaque bloc est SOIT dans sa carte, SOIT dans sa fenêtre : le contenu n'est
    // jamais rendu deux fois. La maquette, elle, le laisse aux deux endroits — mais
    // détacher sert justement à libérer la place que la carte occupait.
    // L'état de départ se lit sur les fenêtres RÉELLEMENT ouvertes : après un
    // remontage, elles sont peut-être encore là.
    const [dashboardDetache, setDashboardDetache] = useState(() => vivante(fenetresDetachees.dashboard));
    const [affichageDetache, setAffichageDetache] = useState(() => vivante(fenetresDetachees.affichage));
    // Pop-ups bloquées : le refus doit se voir à l'écran (leçon du lot 5.3 ter),
    // pas seulement dans la console du navigateur.
    const [detachementErreur, setDetachementErreur] = useState(null);
    const saveTimerRef = useRef(null);

    // ⚠️ LE CURSEUR DE SÉPARATION Dashboard / Filtres A ÉTÉ RETIRÉ (lot 5.5).
    //
    // C'est la décision **D2** du 2026-08-08 : il n'est pas dans la maquette, qui
    // pose une grille fixe (`pl-topzone`, 1.3fr / 1fr). Il mémorisait un ratio par
    // utilisateur dans `localStorage` (`split_<id>`) — un réglage que personne n'a
    // demandé, sur une zone que les deux cartes repliables et le détachement sur un
    // second écran (lot 5.5 bis) remplacent avantageusement.
    //
    // Sont partis avec lui : `splitRatio`, `splitKey`, `dashboardRowRef`,
    // `dividerActive`, `handleDividerMouseDown`, l'effet d'enregistrement différé,
    // et les règles CSS `.dashboard-row` / `.dashboard-left` / `.dashboard-right` /
    // `.dashboard-split-divider` / `.dashboard-section` / `.dashboard-collapse-btn`.
    // La clé `split_<id>` déjà écrite chez un utilisateur devient inerte : on ne la
    // lit plus. La supprimer demanderait un balayage du `localStorage` de chacun
    // pour récupérer quelques octets.

    // ⚠️ Lot 5.1 — « Rendre active » et « Supprimer l'année » ont été RETIRÉS de cet
    // écran. Une année universitaire est transverse à TOUS les départements : la
    // rendre active ou la supprimer depuis l'écran d'un seul département était un
    // acte de gouvernance globale déguisé en réglage local. Les deux gestes vivent
    // dans Administration → Calendriers depuis le lot 3 du Bloc 0 — et depuis
    // 0.B.5, `/api/calendrier` étant en écriture super-administrateur, ces boutons
    // répondaient de toute façon 403 à un chef de département.
    // Le remplacement a été livré AVANT ce retrait, jamais l'inverse.

    // Lot 5.2 — mode consultation. `AppLayout` affichait déjà le bandeau
    // « vous pouvez consulter ce module, pas le modifier », mais l'écran ne
    // l'exploitait pas : les contrôles d'écriture restaient cliquables et c'était
    // le serveur qui refusait en 403. Referme un des cinq points partiels de la
    // recette Administration (« grisage fin des boutons, attend le rétrofit de
    // chaque module »).
    const lectureSeule =
        niveauModule(user, departementCourant(user, config), 'planning') === NIVEAU_CONSULTER;

    // Modales du module — remplacent window.confirm / alert (charte : jamais de
    // boîte de dialogue du navigateur). `confirmation` porte le texte et l'action.
    const [confirmation, setConfirmation] = useState(null);
    const [information, setInformation] = useState(null);
    // Valeur du champ quand la confirmation en porte un (remplace window.prompt)
    const [saisieModale, setSaisieModale] = useState('');

    // ---- Versions restaurables (lot 5.3) ----
    const [versionsOuvert, setVersionsOuvert] = useState(false);
    const [versions, setVersions] = useState([]);
    const [versionsChargement, setVersionsChargement] = useState(false);
    const [saveModaleOuverte, setSaveModaleOuverte] = useState(false);
    const [saveCommentaire, setSaveCommentaire] = useState('');
    const [saveEnCours, setSaveEnCours] = useState(false);
    // Lot 5.3 bis — annulation d'une restauration : l'identifiant de l'instantané
    // technique à rejouer. Session seulement : rien à retrouver après un rechargement.
    const [annulation, setAnnulation] = useState(null);
    // Rappel de sauvegarde : instant de référence (dernière version ou arrivée sur
    // l'écran) et « quelque chose a-t-il changé depuis ».
    const [modifieDepuisVersion, setModifieDepuisVersion] = useState(false);
    const referenceRappelRef = useRef(Date.now());
    const [rappelAffiche, setRappelAffiche] = useState(false);
    // Délai du rappel, lu à la SOURCE par `loadVisibilite` (route des départements,
    // qui renvoie la ligne complète) et non dans `config`, qui perd les réglages
    // selon le chemin de navigation emprunté. 30 = défaut tant que rien n'est chargé.
    const [rappelMinutes, setRappelMinutes] = useState(30);
    const premierChargementEventsRef = useRef(true);
    // Version dont l'écran affiche l'état, après une restauration DANS LA SESSION.
    // Aucun état serveur : après un rechargement, on ne peut pas savoir si l'écran
    // correspond encore à une version — mieux vaut ne rien afficher que mentir.
    const [versionAffichee, setVersionAffichee] = useState(null);

    // Point d'entrée unique des écrans enfants (Calendar, EventManager, picker) :
    // ils demandent une confirmation, ils ne réimplémentent pas de modale.
    const demanderConfirmation = useCallback((demande) => {
        setSaisieModale(demande?.saisie?.valeurInitiale || '');
        setConfirmation(demande);
    }, []);

    // Config effective (remplace anneeScolaireId par l'année sélectionnée dans le menu)
    const effectiveConfig = useMemo(() => ({
        ...config,
        anneeScolaireId: planningAnneeId || config?.anneeScolaireId
    }), [config, planningAnneeId]);

    // Charger la liste de toutes les années
    useEffect(() => {
        calendrierAPI.getAll()
            .then(res => setAnnees(res.data?.annees || []))
            .catch(() => {});
    }, []);

    // Sync depuis la sidebar (prop anneeId externe)
    //
    // Lot 5.2 — la confirmation `window.confirm` qui se trouvait ici a été
    // SUPPRIMÉE, pas seulement restylée. Elle demandait « Continuer ? » alors que
    // le changement d'année venait d'être fait dans la sidebar : répondre non ne
    // revenait pas en arrière, ça laissait la sidebar et le planning sur deux
    // années différentes. On applique donc l'année et on INFORME que l'édition
    // s'est désactivée — ce que faisait déjà, silencieusement, l'effet ci-dessous.
    useEffect(() => {
        if (anneeId && anneeId !== planningAnneeId) {
            const selectedYear = annees.find(a => a.id === anneeId);
            if (selectedYear && !selectedYear.active && editMode) {
                setEditMode(false);
                if (calendarRef.current?.setEditMode) calendarRef.current.setEditMode(false);
                setInformation({
                    titre: 'Mode édition désactivé',
                    message: `${selectedYear.libelle} n'est pas l'année active : le planning s'ouvre en consultation. Revenez sur l'année active pour le modifier.`
                });
            }
            setPlanningAnneeId(anneeId);
        }
    }, [anneeId]);

    // Charger/sauvegarder la visibilité colonnes par (dept + année) dans localStorage
    const visibiliteKey = config?.departement?.id && planningAnneeId
        ? `vis_${config.departement.id}_${planningAnneeId}`
        : null;

    useEffect(() => {
        if (visibiliteKey) {
            const saved = localStorage.getItem(visibiliteKey);
            const vis = saved ? JSON.parse(saved) : {};
            setVisibilite(vis);
            if (calendarRef.current?.setVisibilite) calendarRef.current.setVisibilite(vis);
        }
    }, [planningAnneeId]);

    // Désactiver le mode édition automatiquement si on passe sur une année non active
    useEffect(() => {
        if (anneeScolaire && !anneeScolaire.active && editMode) {
            setEditMode(false);
            if (calendarRef.current?.setEditMode) calendarRef.current.setEditMode(false);
        }
    }, [anneeScolaire]);

    // `handleYearChange` supprimée au lot 5.2 : fonction MORTE. Elle servait un
    // sélecteur d'année interne au Planning, remplacé depuis par celui de la
    // sidebar (prop `anneeId`, effet ci-dessus). Elle portait au passage l'un des
    // `window.confirm` recensés — celui-ci n'avait donc même pas d'appelant.

    const fetchCalendrier = useCallback(async () => {
        const aid = planningAnneeId;
        try {
            let response;
            if (aid) {
                response = await calendrierAPI.getByAnnee(aid);
            } else {
                response = await calendrierAPI.getActif();
            }
            setCalendrier(response.data.calendrier);
            setAnneeScolaire(response.data.anneeScolaire);
        } catch (err) { console.error('Erreur:', err); }
        finally { setLoading(false); }
    }, [planningAnneeId]);

    const loadDashboard = useCallback(async () => {
        const aid = planningAnneeId || config?.anneeScolaireId;
        if (!config?.departement?.id || !aid) return;
        try {
            const response = await dashboardAPI.getData(config.departement.id, aid);
            if (response.data.success) {
                setDashboardData(response.data.dashboard || []);
            }
        } catch (err) {
            console.error("Erreur chargement dashboard:", err);
        }
    }, [config, planningAnneeId]);

    const loadEvents = useCallback(async () => {
        const aid = planningAnneeId || config?.anneeScolaireId;
        if (!config?.departement?.id || !aid) return;
        try {
            const response = await evenementsAPI.getConfig(config.departement.id, {
                anneeScolaireId: aid
            });
            const evts = response.data.evenements || [];
            setPlacedEvents(evts.filter(e => e.date_debut));
        } catch (err) {
            console.error('Erreur chargement événements:', err);
        }
    }, [config, planningAnneeId]);

    /**
     * Les catégories du département (lot 5.4 ter). Elles portent le symbole et la
     * couleur de tous les événements qui n'ont pas de dérogation — c'est-à-dire, la
     * v36 passée, la quasi-totalité d'entre eux. Sans ce chargement, le calendrier
     * afficherait des pastilles grises là où le référentiel annonce des couleurs.
     *
     * Chargées ICI et passées aux enfants plutôt que rechargées par chacun : le
     * Calendrier, le panneau Événements et le sélecteur de date affichent les mêmes
     * événements ; trois requêtes pour la même liste, ce sont trois occasions de
     * l'afficher différemment.
     */
    const loadCategories = useCallback(async () => {
        if (!config?.departement?.id) return;
        try {
            const response = await evenementsAPI.getCategories(config.departement.id);
            setCategoriesEvenements(response.data.categories || []);
        } catch (err) {
            // Le repli est la liste d'amorçage, donc l'écran reste lisible ; on
            // journalise pour que l'incident ne soit pas totalement muet.
            console.error('Erreur chargement catégories:', err);
        }
    }, [config]);

    const loadVisibilite = async () => {
        if (!config?.departement?.id) return;
        try {
            const response = await departementsAPI.getById(config.departement.id);
            const dept = response.data.departement;
            const vis = dept.visibilite_colonnes || {};
            setVisibiliteMois(vis);

            // Le délai du rappel est lu ICI, à la source, et non dans `config` :
            // `config.departement` est reconstruit au fil de la navigation à partir de
            // `user.departements`, qui ne porte que l'identité du département. Un
            // réglage y était donc perdu selon le chemin emprunté pour arriver sur
            // l'écran — enregistré en base, invisible à l'usage (2026-08-08).
            // Cette route renvoie la ligne complète : elle fait autorité.
            const r = parseInt(dept.rappel_sauvegarde_minutes, 10);
            setRappelMinutes(Number.isInteger(r) ? r : 30);
        } catch (err) {
            console.error('Erreur chargement visibilité:', err);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchCalendrier();
        loadEvents();
        loadCategories();
        loadVisibilite();
        loadDashboard();
    }, [fetchCalendrier, loadEvents, loadCategories, loadDashboard]);

    // Lot 5.1 : passe par la route du module Planning et non plus par celle des
    // départements, qui exigeait d'ADMINISTRER le département. Avant ce correctif,
    // un enseignant ayant « modifier » sur le Planning peignait la matrice, la
    // voyait changer à l'écran, recevait un 403 — et rien n'était enregistré.
    // L'échec n'est plus silencieux : il remonte à l'écran.
    const saveVisibilite = useCallback((newVis) => {
        if (!config?.departement?.id) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            try {
                await planningAPI.saveVisibiliteColonnes(config.departement.id, newVis);
                setVisibiliteErreur(null);
            } catch (err) {
                console.error('Erreur sauvegarde visibilité:', err);
                setVisibiliteErreur(
                    err.response?.data?.error
                    || "Le détail par mois n'a pas pu être enregistré — il sera perdu au prochain chargement."
                );
            }
        }, 500);
    }, [config]);

    useEffect(() => {
        window._reloadEvents = loadEvents;
        return () => { delete window._reloadEvents; };
    }, [loadEvents]);

    // ============================================
    // VERSIONS RESTAURABLES (lot 5.3)
    // ============================================

    /** « hier 17:40 », « 12/06 09:10 », « 14:02 » — comme le panneau de la maquette. */
    const formatDateHeure = (iso) => {
        const d = new Date(iso);
        const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        const jour = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const auj = new Date(); const aujJour = new Date(auj.getFullYear(), auj.getMonth(), auj.getDate()).getTime();
        const ecartJours = Math.round((aujJour - jour) / 86400000);
        if (ecartJours === 0) return heure;
        if (ecartJours === 1) return `hier ${heure}`;
        return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${heure}`;
    };

    const loadVersions = useCallback(async () => {
        const aid = planningAnneeId || config?.anneeScolaireId;
        if (!config?.departement?.id || !aid) return;
        setVersionsChargement(true);
        try {
            const r = await planningAPI.getVersions(config.departement.id, aid);
            setVersions(r.data.versions || []);
        } catch (err) {
            console.error('Erreur chargement versions:', err);
            setInformation({
                titre: 'Versions indisponibles',
                message: err.response?.data?.error || "La liste des versions n'a pas pu être chargée."
            });
        } finally {
            setVersionsChargement(false);
        }
    }, [config, planningAnneeId]);

    // Le panneau se remplit à l'ouverture, et se recharge si l'année change pendant
    // qu'il est ouvert — sinon il afficherait les versions d'une autre année.
    useEffect(() => {
        if (versionsOuvert) loadVersions();
    }, [versionsOuvert, loadVersions]);

    /** Le filtre global des colonnes vit en localStorage : c'est nous qui l'envoyons. */
    const filtresColonnesCourants = () => (visibilite && typeof visibilite === 'object' ? visibilite : {});

    const confirmerSauvegardeVersion = async () => {
        const aid = planningAnneeId || config?.anneeScolaireId;
        if (!config?.departement?.id || !aid) return;
        setSaveEnCours(true);
        try {
            const r = await planningAPI.saveVersion(
                config.departement.id, aid, saveCommentaire, filtresColonnesCourants()
            );
            setSaveModaleOuverte(false);
            setSaveCommentaire('');
            setVersionsOuvert(true);
            // Le rappel repart de zéro : on vient d'enregistrer, il n'a plus d'objet.
            referenceRappelRef.current = Date.now();
            setModifieDepuisVersion(false);
            setRappelAffiche(false);
            await loadVersions();
            const v = r.data?.version;
            setInformation({
                titre: 'Version enregistrée',
                message: <>L'état du planning est conservé sous <strong>{v?.libelle}</strong> : {v?.nb_periodes} période(s) et {v?.nb_evenements} date(s) d'événement.</>
            });
        } catch (err) {
            setInformation({
                titre: "La version n'a pas pu être enregistrée",
                message: err.response?.data?.error || err.message || 'Erreur inattendue.'
            });
        } finally {
            setSaveEnCours(false);
        }
    };

    /**
     * Applique une version et remet l'écran d'aplomb.
     * Factorisé au lot 5.3 bis pour que « Annuler la restauration » emprunte
     * exactement le même chemin — deux codes qui font la même chose doivent la faire
     * pareil (leçon du lot 5 du Bloc 0).
     */
    const appliquerVersion = async (v, { estAnnulation }) => {
        try {
            const r = await planningAPI.restaurerVersion(
                v.id, filtresColonnesCourants(),
                // Annuler ne crée pas d'instantané : sinon le bouton se proposerait
                // à lui-même, en « annuler l'annulation ».
                { creerInstantane: !estAnnulation }
            );
            const d = r.data || {};

            // Le serveur ne peut pas écrire dans le localStorage : il nous rend le
            // filtre global, c'est à nous de le reposer.
            if (visibiliteKey && d.filtres_colonnes) {
                localStorage.setItem(visibiliteKey, JSON.stringify(d.filtres_colonnes));
                setVisibilite(d.filtres_colonnes);
                if (calendarRef.current?.setVisibilite) calendarRef.current.setVisibilite(d.filtres_colonnes);
            }

            await Promise.all([fetchCalendrier(), loadEvents(), loadVisibilite(), loadDashboard()]);
            if (calendarRef.current?.reload) calendarRef.current.reload();
            await loadVersions();

            // Après une restauration, l'écran correspond à une version : le rappel
            // repart de zéro. Et l'annulation devient possible (ou cesse de l'être).
            referenceRappelRef.current = Date.now();
            setModifieDepuisVersion(false);
            setRappelAffiche(false);
            setAnnulation(estAnnulation || !d.instantane_avant_id
                ? null
                : { id: d.instantane_avant_id, libelle: v.libelle });
            setVersionAffichee(estAnnulation ? null : v.id);

            const manques = [];
            if (d.evenements_disparus?.length) {
                manques.push(`${d.evenements_disparus.length} événement(s) n'existent plus dans le référentiel : ${d.evenements_disparus.join(', ')}`);
            }
            if (d.groupes_introuvables?.length) {
                manques.push(`${d.groupes_introuvables.length} groupe(s) introuvable(s) : ${d.groupes_introuvables.join(', ')}`);
            }

            setInformation({
                titre: estAnnulation ? 'Restauration annulée' : 'Version restaurée',
                message: (
                    <>
                        <strong>{d.periodes_restaurees}</strong> période(s) et <strong>{d.dates_restaurees}</strong> date(s)
                        d'événement {estAnnulation ? 'ont été remises comme avant.' : 'ont été restaurées.'}
                        {manques.length > 0 && (
                            <>
                                <br /><br />
                                <strong>Ce qui n'a pas pu être restauré :</strong>
                                <ul style={{ margin: '6px 0 0 18px' }}>
                                    {manques.map((m, i) => <li key={i}>{m}</li>)}
                                </ul>
                            </>
                        )}
                        {!estAnnulation && (
                            <>
                                <br />
                                Un bouton <strong>« Annuler la restauration »</strong> est apparu au-dessus du
                                calendrier si ce n'est pas ce que vous vouliez.
                            </>
                        )}
                    </>
                )
            });
        } catch (err) {
            setInformation({
                titre: estAnnulation ? "L'annulation a échoué" : 'La restauration a échoué',
                message: err.response?.data?.error || "Erreur inattendue — aucune donnée n'a été modifiée."
            });
        }
    };

    const restaurerVersion = (v) => {
        setConfirmation({
            titre: 'Restaurer une version',
            message: (
                <>
                    Restaurer <strong>{v.libelle}</strong> du {formatDateHeure(v.created_at)} ?
                    <br /><br />
                    Les périodes et les dates d'événements de l'année affichée seront <strong>remplacées</strong>
                    par celles de cette version ({v.nb_periodes} période(s), {v.nb_evenements} date(s)).
                    Un bouton <strong>« Annuler la restauration »</strong> vous permettra de revenir en un clic.
                    <br /><br />
                    La configuration — types de journées, niveaux, groupes — n'est pas touchée.
                </>
            ),
            libelleConfirmer: 'Restaurer',
            action: () => appliquerVersion(v, { estAnnulation: false })
        });
    };

    // ============================================
    // RAPPEL DE SAUVEGARDE (lot 5.3 bis)
    //
    // Deux conditions, pas une : être en mode ÉDITION **et** avoir modifié quelque
    // chose depuis la dernière version. Un minuteur qui se déclenche alors que rien
    // n'a bougé serait du bruit, et le bruit s'ignore.
    // Réglage par département (`rappel_sauvegarde_minutes`) : 0 = désactivé.
    // ============================================

    /** Appelé quand le planning change (période peinte, événement posé ou retiré). */
    const marquerModifie = useCallback(() => setModifieDepuisVersion(true), []);

    // Secondes restantes avant le rappel — recalculées toutes les 10 secondes.
    // Elles sont AFFICHÉES dans l'indicateur : quand le bandeau ne vient pas, on voit
    // pourquoi (délai réel, compte à rebours arrêté, rappel désactivé) au lieu de se
    // demander si c'est une panne. C'est ce qui manquait le 2026-08-08.
    const [secondesRestantes, setSecondesRestantes] = useState(null);

    useEffect(() => {
        if (rappelMinutes <= 0 || !editMode || !modifieDepuisVersion) {
            setSecondesRestantes(null);
            return;
        }
        const verifier = () => {
            const reste = Math.max(0, rappelMinutes * 60000 - (Date.now() - referenceRappelRef.current));
            setSecondesRestantes(Math.ceil(reste / 1000));
            if (reste <= 0) setRappelAffiche(true);
        };
        verifier();
        const t = setInterval(verifier, 10000);
        return () => clearInterval(t);
    }, [rappelMinutes, editMode, modifieDepuisVersion]);

    /** Fermer le rappel le repousse d'un délai complet — il ne harcèle pas. */
    const reporterRappel = () => {
        referenceRappelRef.current = Date.now();
        setRappelAffiche(false);
    };

    const supprimerVersion = (v) => {
        setConfirmation({
            titre: 'Supprimer une version',
            message: <>Retirer <strong>{v.libelle}</strong> du {formatDateHeure(v.created_at)} de la liste des versions ? Le planning n'est pas modifié.</>,
            libelleConfirmer: 'Supprimer',
            danger: true,
            action: async () => {
                try {
                    await planningAPI.supprimerVersion(v.id);
                    await loadVersions();
                } catch (err) {
                    setInformation({
                        titre: "La version n'a pas pu être supprimée",
                        message: err.response?.data?.error || 'Erreur inattendue.'
                    });
                }
            }
        });
    };

    // Mouseup global pour arrêter le drag de la matrice
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setMoisDragging(false);
            setMoisDragAction(null);
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }, []);

    // ── Reprise des fenêtres détachées, et fermeture en quittant l'écran ─────
    //
    // Au montage : on annule une fermeture programmée par un démontage tout juste
    // précédent (StrictMode), puis on rebranche les fenêtres qu'un rechargement
    // de la page aurait laissées ouvertes.
    //
    // Au démontage : on programme la fermeture — quitter le Planning doit fermer
    // ses fenêtres, elles n'auraient plus rien à afficher — mais À RETARDEMENT,
    // pour qu'un remontage immédiat puisse l'annuler.
    useEffect(() => {
        if (fermetureDifferee) { clearTimeout(fermetureDifferee); fermetureDifferee = null; }

        let memo = [];
        try { memo = JSON.parse(sessionStorage.getItem(CLE_DETACHES) || '[]'); } catch { memo = []; }
        memo.forEach(cle => {
            if (vivante(fenetresDetachees[cle])) return;          // déjà rattachée
            const f = reprendreFenetre(cle);
            if (!f) return;                                       // fenêtre disparue
            fenetresDetachees[cle] = f;
            if (cle === 'dashboard') { setDashboardDetache(true); setDashboardCollapsed(true); }
            else { setAffichageDetache(true); setAffichageCollapsed(true); }
        });
        memoriserDetaches();

        return () => {
            const aFermer = Object.values(fenetresDetachees).filter(vivante);
            fermetureDifferee = setTimeout(() => {
                aFermer.forEach(f => { if (vivante(f)) f.close(); });
                Object.keys(fenetresDetachees).forEach(c => { fenetresDetachees[c] = null; });
                memoriserDetaches();
                fermetureDifferee = null;
            }, 500);
        };
    }, []);

    const handleEventsChange = (evts) => {
        setPlacedEvents(evts.filter(e => e.date_debut));
        // ⚠️ `EventManager` appelle `onEventsChange` AUSSI à son chargement, pas
        // seulement quand l'utilisateur change quelque chose. Sans ce garde, le
        // premier appel allumerait « modifications non enregistrées » alors que rien
        // n'a bougé — un indicateur qui ment vaut moins que pas d'indicateur.
        if (premierChargementEventsRef.current) {
            premierChargementEventsRef.current = false;
            return;
        }
        marquerModifie();
    };

    const handlePastilleClick = (eventId) => {
        setShowEvents(true);
        setHighlightEventId(null);
        setTimeout(() => setHighlightEventId(eventId), 150);
    };

    const handleDateClick = (info) => {
        setDateClickInfo(info);
    };

    // Déplacement d'un événement déjà daté : la confirmation est demandée en modale
    // (lot 5.2), donc de façon asynchrone. `poserEvenement` porte le travail, et
    // `handleDateEventSelect` décide s'il faut demander avant.
    const poserEvenement = async (evt, dateStr) => {
        try {
            if (evt.date_debut) {
                await evenementsAPI.updateDate(evt.date_id, { dateDebut: dateStr });
            } else {
                await evenementsAPI.createDate({
                    evenementId: evt.id,
                    // Lot 5.1 — bug §4 de la spec : c'était `config.anneeScolaireId`,
                    // l'année par DÉFAUT. Après un changement d'année dans la sidebar,
                    // l'événement était rattaché à la mauvaise année : il disparaissait
                    // de l'écran où on venait de le poser et apparaissait ailleurs.
                    anneeScolaireId: effectiveConfig.anneeScolaireId,
                    dateDebut: dateStr
                });
            }
            await loadEvents();
            if (window._reloadEventManager) window._reloadEventManager();
            setDateClickInfo(null);
            marquerModifie();   // alimente le rappel de sauvegarde (lot 5.3 bis)
        } catch (err) {
            console.error('Erreur placement événement:', err);
            setInformation({
                titre: "L'événement n'a pas pu être placé",
                message: err.response?.data?.error || err.message || 'Erreur inattendue.'
            });
        }
    };

    const handleDateEventSelect = (evt) => {
        if (!dateClickInfo) return;
        const dateStr = dateClickInfo.date;
        const enFr = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

        if (evt.date_debut) {
            setDateClickInfo(null);
            setConfirmation({
                titre: 'Déplacer un événement',
                message: <>Déplacer <strong>{evt.libelle}</strong> du {enFr(evt.date_debut)} au {enFr(dateStr)} ?</>,
                libelleConfirmer: 'Déplacer',
                action: () => poserEvenement(evt, dateStr)
            });
            return;
        }
        poserEvenement(evt, dateStr);
    };

    const handleCloseDatePicker = () => { setDateClickInfo(null); };

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') setDateClickInfo(null); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const toggleEditMode = () => {
        const newMode = !editMode;
        setEditMode(newMode);
        if (calendarRef.current?.setEditMode) calendarRef.current.setEditMode(newMode);
    };

    const handleVisibiliteChange = (newVis) => {
        setVisibilite(newVis);
        if (calendarRef.current?.setVisibilite) calendarRef.current.setVisibilite(newVis);
        if (visibiliteKey) localStorage.setItem(visibiliteKey, JSON.stringify(newVis));
    };

    const handleCellStatesChange = useCallback((newStates) => {
        setCellStates(newStates);
    }, []);

    // === Visibilité par mois ===
    const toggleVisibiliteMois = (moisKey, colKey) => {
        const newVis = { ...visibiliteMois };
        if (!newVis[moisKey]) newVis[moisKey] = {};
        if (newVis[moisKey][colKey] === false) {
            delete newVis[moisKey][colKey];
            if (Object.keys(newVis[moisKey]).length === 0) delete newVis[moisKey];
        } else {
            newVis[moisKey][colKey] = false;
        }
        setVisibiliteMois(newVis);
        saveVisibilite(newVis);
    };

    const isVisibleMois = (moisKey, colKey) => {
        if (!visibiliteMois[moisKey]) return true;
        return visibiliteMois[moisKey][colKey] !== false;
    };

    const setAllVisibiliteMois = (value) => {
        if (value) {
            setVisibiliteMois({});
            saveVisibilite({});
        } else {
            const newVis = {};
            moisList.forEach(moisKey => {
                newVis[moisKey] = {};
                colonnes.forEach(col => {
                    newVis[moisKey][col.niveauCode + '-' + col.code] = false;
                });
            });
            setVisibiliteMois(newVis);
            saveVisibilite(newVis);
        }
    };

    const handleMoisCellMouseDown = (moisKey, colKey) => {
        const visible = isVisibleMois(moisKey, colKey);
        const action = visible ? 'hide' : 'show';
        setMoisDragging(true);
        setMoisDragAction(action);
        toggleVisibiliteMois(moisKey, colKey);
    };

    const handleMoisCellMouseEnter = (moisKey, colKey) => {
        if (!moisDragging) return;
        const visible = isVisibleMois(moisKey, colKey);
        if (moisDragAction === 'hide' && visible) {
            toggleVisibiliteMois(moisKey, colKey);
        } else if (moisDragAction === 'show' && !visible) {
            toggleVisibiliteMois(moisKey, colKey);
        }
    };

    // Colonnes pour les filtres
    const colonnes = [];
    if (effectiveConfig?.niveaux) {
        effectiveConfig.niveaux.forEach(niveau => {
            if (niveau.colonnes) {
                niveau.colonnes.forEach(col => {
                    colonnes.push({
                        niveauId: niveau.id, niveauCode: niveau.code,
                        code: col.code, label: col.label, couleur: col.couleur || '#007BFF'
                    });
                });
            }
        });
    }
    const niveauxUniques = [...new Set(colonnes.map(c => c.niveauCode))];
    const isVisible = (key) => visibilite[key] !== false;

    const toggleVisibiliteSingle = (key) => {
        const newVis = { ...visibilite, [key]: visibilite[key] === false };
        handleVisibiliteChange(newVis);
    };

    const toggleNiveau = (code) => {
        const cols = colonnes.filter(c => c.niveauCode === code);
        const allVis = cols.every(c => isVisible(c.niveauCode + '-' + c.code));
        const newVis = { ...visibilite };
        cols.forEach(c => { newVis[c.niveauCode + '-' + c.code] = allVis ? false : undefined; });
        Object.keys(newVis).forEach(k => { if (newVis[k] === undefined) delete newVis[k]; });
        handleVisibiliteChange(newVis);
    };

    // Liste des mois du calendrier
    const moisList = [];
    const moisLabels = {};
    if (calendrier) {
        calendrier.forEach(jour => {
            const key = jour.nom_mois.toLowerCase() + '-' + jour.annee;
            if (!moisLabels[key]) {
                moisLabels[key] = jour.nom_mois.substring(0, 3).toUpperCase();
                moisList.push(key);
            }
        });
    }

    if (loading) return (<div className="loading"><div className="spinner"></div><p>Chargement...</p></div>);

    // ═══ LOT 5.5 bis — DÉTACHEMENT SUR UN SECOND ÉCRAN ═══════════════════════
    // Les deux blocs de la zone haute sont construits ICI, une seule fois, et
    // rendus SOIT dans leur carte, SOIT dans leur fenêtre. Le même élément React
    // aux deux endroits, ce serait deux états à tenir d'accord ; un seul endroit
    // à la fois, c'est la synchronisation par construction.
    const detacher = (quoi) => {
        setDetachementErreur(null);

        // ⚠️ `window.open` EST APPELÉ ICI, dans la pile d'appel du clic — jamais
        // depuis un `useEffect`. Un navigateur n'ouvre une fenêtre que si elle est
        // demandée pendant le traitement d'un geste de l'utilisateur ; après le
        // rendu, ce crédit est consommé et Chrome bloque. C'est le défaut trouvé
        // par Bertrand à la recette : « le navigateur bloque » alors que la
        // maquette fonctionne — elle appelle `detachWin` directement dans son
        // `onclick`. Le composant `FenetreDetachee` reçoit donc une fenêtre déjà
        // ouverte, et ne fait que l'habiller.
        // ⚠️ POSITIONS DISTINCTES, ET C'EST INDISPENSABLE. Sans `left`/`top`, le
        // navigateur place les deux fenêtres au MÊME endroit : la seconde recouvre
        // la première au pixel près, et on croit que la première s'est fermée.
        // Or les deux doivent pouvoir vivre côte à côte sur le second écran —
        // c'est tout l'objet du détachement.
        const geo = quoi === 'dashboard'
            ? { l: 1000, h: 740, x: 40,  y: 40 }
            : { l: 900,  h: 680, x: 200, y: 180 };
        const fenetre = window.open('', 'pl_' + quoi,
            `width=${geo.l},height=${geo.h},left=${geo.x},top=${geo.y}`);

        if (!fenetre) {
            setDetachementErreur(
                "La fenêtre détachée n'a pas pu s'ouvrir : votre navigateur bloque les fenêtres surgissantes "
                + "pour ce site. Autorisez-les, puis réessayez — en attendant, le bloc reste affiché dans sa carte."
            );
            return;
        }

        // Garde-fou : si le navigateur renvoyait la fenêtre DÉJÀ utilisée par
        // l'autre bloc, deux composants piloteraient le même document et le
        // premier verrait le sien disparaître sans explication. On le dit plutôt
        // que de laisser deviner.
        const autre = quoi === 'dashboard' ? 'affichage' : 'dashboard';
        if (fenetresDetachees[autre] === fenetre) {
            setDetachementErreur(
                "Votre navigateur a réutilisé la fenêtre déjà détachée au lieu d'en ouvrir une seconde. "
                + "Ramenez l'autre bloc, puis détachez celui-ci."
            );
            return;
        }

        fenetresDetachees[quoi] = fenetre;
        memoriserDetaches();
        fenetre.focus();
        if (quoi === 'dashboard') { setDashboardDetache(true); setDashboardCollapsed(true); }
        else { setAffichageDetache(true); setAffichageCollapsed(true); }
    };

    // La carte revient REPLIÉE (choix de Bertrand, 2026-08-09). J'avais fait
    // l'inverse — redéplier — en pensant qu'un bloc ramené puis laissé replié
    // passerait pour perdu. C'est le contraire : la zone haute naît repliée pour
    // laisser la place au calendrier, et ramener un bloc n'est pas une raison de
    // la lui reprendre. Le chevron redevient cliquable, à sa discrétion.
    //
    // C'est ICI que la fenêtre se ferme, et nulle part ailleurs : elle appartient
    // à qui l'a ouverte. Confier sa fermeture au nettoyage d'effet du composant
    // la faisait fermer aussitôt ouverte, `React.StrictMode` démontant et
    // remontant chaque composant en développement.
    const ramener = (quoi) => {
        const fenetre = fenetresDetachees[quoi];
        fenetresDetachees[quoi] = null;
        if (vivante(fenetre)) fenetre.close();
        memoriserDetaches();
        if (quoi === 'dashboard') { setDashboardDetache(false); setDashboardCollapsed(true); }
        else { setAffichageDetache(false); setAffichageCollapsed(true); }
    };

    // Bouton « Détacher » / « Ramener » d'une carte de la zone haute.
    const boutonDetachement = (quoi, estDetache) => (
        <button
            className="pl-btn pl-btn-sm"
            onClick={() => (estDetache ? ramener(quoi) : detacher(quoi))}
            title={estDetache
                ? 'Refermer la fenêtre et remettre le bloc dans sa carte'
                : 'Détacher sur un autre écran'}
        >
            <i className={'fa-solid ' + (estDetache ? 'fa-arrow-right-to-bracket' : 'fa-up-right-from-square')} />
            {estDetache ? ' Ramener' : ' Détacher'}
        </button>
    );

    // Mention portée par l'en-tête d'une carte dont le contenu est ailleurs.
    const mentionDetache = (
        <span className="pl-detache-tag">
            <i className="fa-solid fa-window-restore" /> affiché dans la fenêtre détachée
        </span>
    );

    // Même contexte que le sous-titre de la topbar : département · année.
    const contexteDetache = (config?.departement?.nom || '')
        + (anneeScolaire?.libelle ? ` · ${anneeScolaire.libelle}` : '');

    const blocTableauBord = <DashboardCompact data={dashboardData} />;

    // Le bloc « Affichage » (filtres de colonnes + détail par mois) est construit
    // ici, en variable, pour être rendu SOIT dans sa carte, SOIT dans sa fenêtre
    // détachée — jamais aux deux endroits. Il vivait en ligne dans le JSX de la
    // carte jusqu'au lot 5.5.
    const blocAffichage = colonnes.length > 0 ? (
        <div className="filter-panel">
            <div className="filter-niveaux-grid">
                {niveauxUniques.map(code => {
                    const cols = colonnes.filter(c => c.niveauCode === code);
                    const allVis = cols.every(c => isVisible(c.niveauCode + '-' + c.code));
                    const hasGroups = cols.length > 1;
                    return (
                        <div key={code} className="filter-niveau-column">
                            <button
                                className={'filter-btn filter-niveau-btn ' + (allVis ? 'filter-active' : 'filter-hidden')}
                                onClick={() => toggleNiveau(code)}
                            >
                                <i className={'fa-solid ' + (allVis ? 'fa-eye' : 'fa-eye-slash')} /> {code}
                            </button>
                            {hasGroups && (
                                <div className="filter-groupes-list">
                                    {cols.map((col, i) => {
                                        const key = col.niveauCode + '-' + col.code;
                                        // Lot 5.5 : le libellé perd le préfixe de son
                                        // niveau — « BUT1 FI » sous le bouton BUT1 se
                                        // lit « FI ». Le niveau est déjà écrit juste
                                        // au-dessus ; le répéter sur chaque groupe
                                        // allonge les boutons sans rien apprendre.
                                        // Repli sur le libellé entier s'il ne reste
                                        // rien : un groupe qui s'appelle exactement
                                        // comme son niveau doit rester nommé.
                                        const libelle = (col.label || '')
                                            .replace(col.niveauCode, '').trim() || col.label;
                                        return (
                                            <button
                                                key={i}
                                                className={'filter-btn filter-groupe-btn ' + (isVisible(key) ? 'filter-active' : 'filter-hidden')}
                                                style={isVisible(key) ? { borderColor: col.couleur, color: col.couleur } : {}}
                                                onClick={() => toggleVisibiliteSingle(key)}
                                                title={col.label}
                                            >
                                                <i className={'fa-solid ' + (isVisible(key) ? 'fa-eye' : 'fa-eye-slash')} /> {libelle}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Bouton dépliant détail par mois */}
            <button
                className="mois-detail-toggle"
                onClick={() => setMoisDetailOpen(!moisDetailOpen)}
            >
                <i className={'fa-solid ' + (moisDetailOpen ? 'fa-chevron-down' : 'fa-chevron-right')} /> Détail par mois
            </button>

            {/* Lot 5.1 : avant, un enregistrement refusé finissait dans
                un console.error — la matrice paraissait enregistrée et
                revenait à son état d'avant au rechargement suivant. */}
            {visibiliteErreur && (
                <div style={{
                    marginTop: 8, padding: '6px 10px', borderRadius: 6,
                    background: '#fef3c7', color: '#92400e',
                    border: '1px solid #f59e0b', fontSize: 11.5,
                    display: 'flex', alignItems: 'center', gap: 7
                }}>
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>{visibiliteErreur}</span>
                </div>
            )}

            {/* Matrice visibilité par mois */}
            {moisDetailOpen && moisList.length > 0 && (
                <div className="mois-matrix-section">
                    <div className="mois-matrix-wrapper">
                        <table className="mois-matrix">
                            <thead>
                                <tr>
                                    <th className="mois-matrix-corner"></th>
                                    {moisList.map(moisKey => (
                                        <th key={moisKey} className="mois-matrix-mois-header">
                                            {moisLabels[moisKey]}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {colonnes.map((col, cIdx) => {
                                    const colKey = col.niveauCode + '-' + col.code;
                                    return (
                                        <tr key={cIdx}>
                                            <td className="mois-matrix-label">
                                                <span className="mois-matrix-dot" style={{ background: col.couleur }}></span>
                                                <span className="mois-matrix-label-text">{col.label}</span>
                                            </td>
                                            {moisList.map(moisKey => {
                                                const visible = isVisibleMois(moisKey, colKey);
                                                return (
                                                    <td
                                                        key={moisKey}
                                                        className={'mois-matrix-cell ' + (visible ? 'mois-cell-visible' : 'mois-cell-hidden')}
                                                        style={visible ? { background: col.couleur } : {}}
                                                        onMouseDown={(e) => { e.preventDefault(); handleMoisCellMouseDown(moisKey, colKey); }}
                                                        onMouseEnter={() => handleMoisCellMouseEnter(moisKey, colKey)}
                                                        title={(visible ? 'Masquer' : 'Afficher') + ' ' + col.label + ' en ' + moisLabels[moisKey]}
                                                    >
                                                        {visible ? <i className="fa-solid fa-check" /> : null}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="mois-matrix-actions">
                        <button className="btn btn-success btn-small" onClick={() => setAllVisibiliteMois(true)}>Tout afficher</button>
                        <button className="btn btn-secondary btn-small" onClick={() => setAllVisibiliteMois(false)}>Tout masquer</button>
                    </div>
                </div>
            )}
        </div>
    ) : null;

    return (
        <div className="pl-root">
            {/* ===== ZONE FIXE : topbar + dashboard, pleine largeur ===== */}
            <div className="sticky-top" style={{ margin: 0, top: 0 }}>
                {/* ===== TOPBAR (lot 5.2, à la maquette ; factorisée au lot 5.4) =====
                    Elle vit maintenant dans `PlanningTopbar` : le Paramétrage en
                    affichait une copie, et deux barres pour un seul module finissent
                    toujours par diverger. */}
                <PlanningTopbar
                    sousTitre={
                        (config?.departement?.nom || '') +
                        (anneeScolaire?.libelle ? ` · ${anneeScolaire.libelle}` : '')
                    }
                    onglet={onglet}
                    onChangeOnglet={onChangeOnglet}
                >
                        <button
                            className={'pl-btn ' + (editMode ? 'pl-btn-success' : '')}
                            onClick={toggleEditMode}
                            disabled={lectureSeule}
                            title={lectureSeule
                                ? "Vous consultez ce module sans pouvoir le modifier : l'édition est désactivée"
                                : (editMode ? 'Désactiver le mode édition' : 'Activer le mode édition')}
                        >
                            <i className={'fa-solid ' + (editMode ? 'fa-lock-open' : 'fa-lock')} />
                            {editMode ? 'Édition activée' : 'Édition'}
                        </button>

                        <button
                            className={'pl-btn ' + (showEvents ? 'pl-btn-success' : '')}
                            onClick={() => setShowEvents(!showEvents)}
                        >
                            <i className="fa-solid fa-thumbtack" /> Événements
                        </button>

                        {/* Lot 5.3 — le libellé « Sauvegarder la config. » est celui de la
                            maquette (règle 4). Il est ambigu : ce bouton n'enregistre PAS le
                            Paramétrage, il crée une VERSION de l'état du planning. L'infobulle
                            et le texte de la modale lèvent l'ambiguïté (décision Bertrand). */}
                        <button
                            className="pl-btn"
                            onClick={() => { setSaveCommentaire(''); setSaveModaleOuverte(true); }}
                            disabled={lectureSeule}
                            title={lectureSeule
                                ? 'Vous consultez ce module sans pouvoir le modifier'
                                : "Enregistre l'état actuel du planning (périodes, événements placés, filtres) comme version restaurable"}
                        >
                            <i className="fa-solid fa-floppy-disk" /> Sauvegarder la config.
                        </button>
                        <button
                            className={'pl-btn pl-btn-icon ' + (versionsOuvert ? 'pl-btn-success' : '')}
                            onClick={() => setVersionsOuvert(!versionsOuvert)}
                            title="Versions du planning"
                        >
                            <i className="fa-solid fa-clock-rotate-left" />
                        </button>

                        {/* Indicateur « modifications non enregistrées » (ajouté après le
                            test du 2026-08-08). Le rappel exige DEUX conditions — être en
                            édition ET avoir modifié quelque chose — et rien à l'écran ne
                            permettait de savoir si elles étaient réunies : quand le bandeau
                            n'apparaissait pas, impossible de dire si c'était un défaut ou
                            une condition non remplie. Ce point rend l'état visible en
                            permanence, et son infobulle annonce le délai réglé. */}
                        {modifieDepuisVersion && (
                            <span
                                className="pl-bandeau pl-bandeau-warn"
                                style={{ margin: 0, padding: '4px 9px' }}
                                title={rappelMinutes > 0
                                    ? `Rappel réglé sur ${rappelMinutes} minute(s) — modifiable dans Paramétrage.`
                                    : 'Rappel de sauvegarde désactivé pour ce département (Paramétrage → Rappel de sauvegarde).'}
                            >
                                <i className="fa-solid fa-circle" style={{ fontSize: 6 }} />
                                modifications non enregistrées
                                {/* Le compte à rebours est AFFICHÉ, et pas seulement calculé :
                                    c'est lui qui permet de voir, sans ouvrir la console, que le
                                    délai lu est le bon et que le minuteur tourne. Sans ça, un
                                    bandeau qui n'arrive pas est indiscernable d'une panne. */}
                                {rappelMinutes > 0 && secondesRestantes !== null && (
                                    <> · rappel dans {secondesRestantes >= 60
                                        ? `${Math.ceil(secondesRestantes / 60)} min`
                                        : `${secondesRestantes} s`}</>
                                )}
                                {rappelMinutes <= 0 && <> · rappel désactivé</>}
                            </span>
                        )}

                        {/* Les DEUX boutons d'export ont quitté cette barre, chacun
                            dans le commit qui livrait son remplacement, jamais avant
                            (décision D4) : « Export Calendriers » et `ExportPDF.js` au
                            lot 5.6, « Export Dates » et `ExportDates.js` au lot 5.7.
                            La topbar porte désormais les quatre onglets du dessin. */}
                </PlanningTopbar>

                {/* ===== ZONE HAUTE (lots 5.5 et 5.5 bis) =====
                    Deux cartes à la charte, repliables INDÉPENDAMMENT, dans la grille
                    fixe de la maquette (1.3fr / 1fr). Le chevron est DANS le titre et
                    c'est le titre entier qui replie — comme le dessin, et comme les
                    accordéons du reste du module.

                    Lot 5.5 bis : chaque carte porte « Détacher », qui ouvre une VRAIE
                    fenêtre (`FenetreDetachee`) déplaçable sur un second écran. La carte
                    se replie alors et le dit ; le bouton devient « Ramener ». C'est le
                    remplacement de « Agrandir » — et de sa modale `DashboardModal`,
                    retirée dans le même lot, pas avant. Écart E18 refermé. */}
                {/* Lot PA.2 : après « Préparer l'année », les dates qui tombent mal */}
                <PlanningAVerifier departementId={config?.departement?.id} peutEcrire={!lectureSeule} />
                <div className="pl-topzone">
                    <div className="pl-card">
                        <div className="pl-card-header">
                            <span
                                className={'ttl' + (dashboardDetache ? '' : ' pl-repliable')}
                                onClick={dashboardDetache ? undefined : () => setDashboardCollapsed(!dashboardCollapsed)}
                                title={dashboardDetache
                                    ? 'Ce bloc est affiché dans sa fenêtre détachée'
                                    : (dashboardCollapsed ? 'Déplier' : 'Replier')}
                            >
                                {!dashboardDetache && (
                                    <i className={'fa-solid ' + (dashboardCollapsed ? 'fa-chevron-right' : 'fa-chevron-down')} />
                                )}
                                {' '}<i className="fa-solid fa-gauge-high" /> Tableau de bord
                            </span>
                            {dashboardDetache && mentionDetache}
                            <span style={{ display: 'flex', gap: 6 }}>
                                <button
                                    className="pl-btn pl-btn-sm"
                                    onClick={loadDashboard}
                                    title="Rafraîchir"
                                >
                                    <i className="fa-solid fa-rotate" />
                                </button>
                                {boutonDetachement('dashboard', dashboardDetache)}
                            </span>
                        </div>
                        {!dashboardDetache && !dashboardCollapsed && (
                            <div className="pl-card-body pl-topzone-body">
                                {blocTableauBord}
                            </div>
                        )}
                    </div>

                    <div className="pl-card">
                        <div className="pl-card-header">
                            <span
                                className={'ttl' + (affichageDetache ? '' : ' pl-repliable')}
                                onClick={affichageDetache ? undefined : () => setAffichageCollapsed(!affichageCollapsed)}
                                title={affichageDetache
                                    ? 'Ce bloc est affiché dans sa fenêtre détachée'
                                    : (affichageCollapsed ? 'Déplier' : 'Replier')}
                            >
                                {!affichageDetache && (
                                    <i className={'fa-solid ' + (affichageCollapsed ? 'fa-chevron-right' : 'fa-chevron-down')} />
                                )}
                                {' '}<i className="fa-solid fa-eye" /> Affichage
                            </span>
                            {affichageDetache && mentionDetache}
                            {boutonDetachement('affichage', affichageDetache)}
                        </div>
                        {!affichageDetache && !affichageCollapsed && (
                            <div className="pl-card-body">
                                {blocAffichage}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ===== LES DEUX FENÊTRES DÉTACHÉES (lot 5.5 bis) =====
                Rendues hors de la zone haute : ce ne sont pas des enfants visuels de
                la page, elles n'y occupent aucune place. Leur contenu est LE MÊME
                élément React que celui de la carte — d'où la synchronisation. */}
            {dashboardDetache && (
                <FenetreDetachee
                    fenetre={fenetresDetachees.dashboard}
                    titre="Tableau de bord"
                    sousTitre={contexteDetache}
                    icone="fa-gauge-high"
                    onFermer={() => ramener('dashboard')}
                    actions={
                        // Rafraîchir depuis la fenêtre : sans ce bouton, il faut
                        // revenir à la page pour mettre à jour ce qu'on regarde ici.
                        <button className="pl-btn pl-btn-sm" onClick={loadDashboard} title="Rafraîchir">
                            <i className="fa-solid fa-rotate" /> Rafraîchir
                        </button>
                    }
                >
                    {blocTableauBord}
                </FenetreDetachee>
            )}
            {affichageDetache && (
                <FenetreDetachee
                    fenetre={fenetresDetachees.affichage}
                    titre="Affichage — colonnes et détail par mois"
                    sousTitre={contexteDetache}
                    icone="fa-eye"
                    onFermer={() => ramener('affichage')}
                >
                    {blocAffichage}
                </FenetreDetachee>
            )}


            {/* ===== CALENDRIER + LÉGENDE + PANNEAU VERSIONS ===== */}
            <div className="pl-layout" style={{ padding: '0 20px 20px' }}>
              <div className="pl-main">

                {/* Pop-ups bloquées (lot 5.5 bis). Le refus du navigateur est silencieux
                    — `window.open` renvoie `null` et c'est tout. Sans ce bandeau, le
                    bouton « Détacher » paraîtrait en panne. */}
                {detachementErreur && (
                    <div className="pl-bandeau pl-bandeau-warn" style={{ marginBottom: 10 }}>
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span style={{ flex: 1 }}>{detachementErreur}</span>
                        <button className="pl-side-act" onClick={() => setDetachementErreur(null)} title="Masquer">
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>
                )}

                {/* Annuler la restauration (lot 5.3 bis) — remplace la ligne parasite
                    que la restauration ajoutait dans la liste des versions. */}
                {annulation && (
                    <div className="pl-bandeau pl-bandeau-info" style={{ marginBottom: 10 }}>
                        <i className="fa-solid fa-clock-rotate-left" />
                        <span style={{ flex: 1 }}>
                            Vous avez restauré <strong>{annulation.libelle}</strong>. Ce n'était pas voulu ?
                        </span>
                        <button className="pl-btn" onClick={() => appliquerVersion({ id: annulation.id, libelle: annulation.libelle }, { estAnnulation: true })}>
                            <i className="fa-solid fa-rotate-left" /> Annuler la restauration
                        </button>
                        <button className="pl-side-act" onClick={() => setAnnulation(null)} title="Masquer">
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>
                )}

                {/* Rappel de sauvegarde (lot 5.3 bis) */}
                {rappelAffiche && (
                    <div className="pl-bandeau pl-bandeau-warn" style={{ marginBottom: 10 }}>
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span style={{ flex: 1 }}>
                            Vous modifiez le planning depuis plus de {rappelMinutes} minutes sans enregistrer
                            de version.
                        </span>
                        <button className="pl-btn" onClick={() => { setSaveCommentaire(''); setSaveModaleOuverte(true); }}>
                            <i className="fa-solid fa-floppy-disk" /> Sauvegarder maintenant
                        </button>
                        <button className="pl-side-act" onClick={reporterRappel} title="Plus tard">
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>
                )}

                <Calendar
                    calendrier={calendrier}
                    config={effectiveConfig}
                    placedEvents={placedEvents}
                    categoriesEvenements={categoriesEvenements}
                    visibiliteMois={visibiliteMois}
                    onPastilleClick={handlePastilleClick}
                    onDateClick={handleDateClick}
                    onCellStatesChange={handleCellStatesChange}
                    onPeriodeChange={() => { marquerModifie(); loadDashboard(); }}
                    onDemanderConfirmation={demanderConfirmation}
                    onErreur={(msg) => setInformation({
                        titre: "Une modification n'a pas pu être enregistrée",
                        message: <>{msg}<br /><br />Votre écran affiche la modification, mais elle <strong>n'est pas en base</strong> : elle disparaîtra au prochain chargement.</>
                    })}
                    ref={calendarRef}
                />
                <Legend typesJournees={effectiveConfig?.typesJournees} />
              </div>

              {/* Panneau Versions — pousse le calendrier au lieu de le recouvrir, pour
                  pouvoir comparer une version avec ce qu'on a sous les yeux. */}
              <div className={'pl-side' + (versionsOuvert ? ' open' : '')}>
                <div className="pl-side-h">
                    <i className="fa-solid fa-clock-rotate-left" /> Versions du planning
                    <button className="pl-side-act" style={{ marginLeft: 'auto' }} onClick={loadVersions} title="Rafraîchir">
                        <i className={'fa-solid fa-rotate' + (versionsChargement ? ' fa-spin' : '')} />
                    </button>
                    <button className="pl-side-act" onClick={() => setVersionsOuvert(false)} title="Fermer">
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>
                <div className="pl-side-b">
                    {versions.length === 0 ? (
                        <div className="pl-ver-vide">
                            Aucune version enregistrée pour cette année.<br />
                            Cliquez <b>Sauvegarder la config.</b> avant une modification importante :
                            vous pourrez revenir à cet état.
                        </div>
                    ) : versions.map((v, i) => (
                        <div key={v.id} className={'pl-ver' + (i === 0 ? ' cur' : '')}>
                            {/* Lot 5.3 bis : restaurer et supprimer sont proposés sur TOUTES
                                les versions. « Revenir à ma dernière sauvegarde » est le geste
                                le plus utile, et le `i > 0` de la maquette l'interdisait. */}
                            <div className="pl-ver-h">
                                <b>{formatDateHeure(v.created_at)}</b>
                                {i === 0 && (
                                    <span className="pl-ver-badge"><i className="fa-solid fa-circle" style={{ fontSize: 6 }} /> la plus récente</span>
                                )}
                                {versionAffichee === v.id && (
                                    <span className="pl-ver-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                                        <i className="fa-solid fa-eye" /> affichée
                                    </span>
                                )}
                                <button className="pl-ver-del" onClick={() => supprimerVersion(v)} title="Retirer du fil">
                                    <i className="fa-solid fa-trash" />
                                </button>
                            </div>
                            <div className="pl-ver-m">{v.libelle}</div>
                            <div className="pl-ver-w">
                                <span><i className="fa-solid fa-user" /> {v.auteur}</span>
                                <span title="périodes / dates d'événements conservées">
                                    · {v.nb_periodes}p · {v.nb_evenements}é
                                </span>
                                {!lectureSeule && (
                                    <button className="pl-ver-restore" onClick={() => restaurerVersion(v)}>
                                        <i className="fa-solid fa-rotate-left" /> restaurer
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
              </div>
            </div>

            <EventManager
                config={effectiveConfig}
                isOpen={showEvents}
                onClose={() => setShowEvents(false)}
                onEventsChange={handleEventsChange}
                highlightEventId={highlightEventId}
                anneeId={planningAnneeId}
                annees={annees}
                onDemanderConfirmation={demanderConfirmation}
                categories={categoriesEvenements}
            />

            {dateClickInfo && (
                <DateEventPicker
                    config={effectiveConfig}
                    dateInfo={dateClickInfo}
                    onSelect={handleDateEventSelect}
                    onClose={handleCloseDatePicker}
                    onDemanderConfirmation={demanderConfirmation}
                    categories={categoriesEvenements}
                />
            )}

            {/* `DashboardModal` a été SUPPRIMÉ au lot 5.5 bis, en même temps que le
                bouton « Agrandir » qui l'ouvrait : la fenêtre détachée est son
                remplacement, et elle est livrée dans le même lot. Rien n'a été retiré
                avant d'avoir obtenu ce qui le remplace. */}

            {/* ===== MODALE « SAUVEGARDER LA CONFIGURATION » (lot 5.3) ===== */}
            {saveModaleOuverte && (
                <ModalePlanning
                    titre="Sauvegarder la configuration"
                    icone="fa-floppy-disk"
                    onFermer={() => setSaveModaleOuverte(false)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setSaveModaleOuverte(false)}>Annuler</button>
                            {/* Lot 5.3 bis : le commentaire est OBLIGATOIRE, et le bouton
                                dit POURQUOI il est inactif — un bouton grisé muet a déjà
                                bloqué un test au lot 3 du Bloc 0. */}
                            <button
                                className="pl-btn pl-btn-primary"
                                onClick={confirmerSauvegardeVersion}
                                disabled={saveEnCours || !saveCommentaire.trim()}
                                title={!saveCommentaire.trim()
                                    ? 'Saisissez un commentaire : c\'est lui qui vous permettra de reconnaître cette version'
                                    : undefined}
                            >
                                <i className={'fa-solid ' + (saveEnCours ? 'fa-spinner fa-spin' : 'fa-floppy-disk')} />
                                {saveEnCours ? 'Enregistrement…' : 'Enregistrer la version'}
                            </button>
                        </>
                    }
                >
                    Enregistre l'<strong>état actuel du planning</strong> — périodes, événements placés et
                    filtres d'affichage — pour pouvoir y revenir. La configuration (types de journées,
                    niveaux, groupes) n'est pas concernée : elle se gère au Paramétrage.
                    <div style={{ marginTop: 12 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
                            Commentaire
                        </label>
                        <input
                            type="text"
                            autoFocus
                            value={saveCommentaire}
                            maxLength={250}
                            placeholder="ex. avant ajustement alternance BUT2"
                            onChange={e => setSaveCommentaire(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !saveEnCours) confirmerSauvegardeVersion(); }}
                            style={{ width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'inherit' }}
                        />
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                            <strong>Obligatoire</strong> — c'est ce texte que vous verrez dans la liste
                            pour choisir quelle version restaurer. Les 15 dernières sont conservées.
                        </div>
                    </div>
                </ModalePlanning>
            )}

            {/* ===== MODALES DU MODULE (lot 5.2) =====
                Remplacent window.confirm et alert. Une seule paire pour tout le
                module : les écrans enfants remontent leur demande plutôt que d'en
                réimplémenter une chacun. */}
            {confirmation && (
                <ModalePlanning
                    titre={confirmation.titre}
                    icone={confirmation.danger ? 'fa-triangle-exclamation' : 'fa-circle-question'}
                    onFermer={() => setConfirmation(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setConfirmation(null)}>Annuler</button>
                            <button
                                className={'pl-btn ' + (confirmation.danger ? 'pl-btn-danger' : 'pl-btn-primary')}
                                disabled={!!confirmation.saisie && !saisieModale.trim()}
                                title={confirmation.saisie && !saisieModale.trim() ? 'Saisissez une valeur pour continuer' : undefined}
                                onClick={() => {
                                    const a = confirmation.action;
                                    const v = saisieModale.trim();
                                    setConfirmation(null);
                                    if (a) a(v);
                                }}
                            >
                                <i className="fa-solid fa-check" /> {confirmation.libelleConfirmer || 'Confirmer'}
                            </button>
                        </>
                    }
                >
                    {confirmation.message}
                    {confirmation.saisie && (
                        <div style={{ marginTop: 12 }}>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
                                {confirmation.saisie.label}
                            </label>
                            <input
                                type="text"
                                autoFocus
                                value={saisieModale}
                                placeholder={confirmation.saisie.placeholder || ''}
                                onChange={e => setSaisieModale(e.target.value)}
                                style={{ width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'inherit' }}
                            />
                        </div>
                    )}
                </ModalePlanning>
            )}

            {information && (
                <ModalePlanning
                    titre={information.titre}
                    icone="fa-circle-info"
                    onFermer={() => setInformation(null)}
                    pied={<button className="pl-btn pl-btn-primary" onClick={() => setInformation(null)}>J'ai compris</button>}
                >
                    {information.message}
                </ModalePlanning>
            )}

        </div>
    );
}

// `ModalePlanning` vivait ici, non exportée — d'où une deuxième copie du même
// balisage dans le Paramétrage. Extraite dans `ModalePlanning.js` au lot 5.4 bis,
// au moment où le référentiel des événements allait en écrire une troisième.

// ============================================
// DateEventPicker
// ============================================
function DateEventPicker({ config, dateInfo, onSelect, onClose, onDemanderConfirmation, categories }) {
    const [evenements, setEvenements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newLibelle, setNewLibelle] = useState('');
    const [newCategorie, setNewCategorie] = useState(CODE_FILET);
    const pickerRef = useRef(null);

    // Lot 5.4 ter : la liste des catégories vient du RÉFÉRENTIEL du département,
    // plus d'une constante figée. Sans ça, créer un événement depuis ce popover
    // proposerait huit familles pendant que le Paramétrage en affiche dix — dont
    // deux que l'utilisateur vient lui-même de créer.
    const CATEGORIES = categories?.length ? categories : CATEGORIES_AMORCAGE;

    useEffect(() => { loadEvenements(); }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose();
        };
        setTimeout(() => window.addEventListener('mousedown', handleClickOutside), 100);
        return () => window.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const loadEvenements = async () => {
        try {
            const response = await evenementsAPI.getConfig(config.departement.id, { anneeScolaireId: config.anneeScolaireId });
            setEvenements(response.data.evenements || []);
        } catch (err) { console.error('Erreur:', err); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        if (!newLibelle.trim()) return;
        try {
            await evenementsAPI.createConfig({ departementId: config.departement.id, libelle: newLibelle.trim(), categorie: newCategorie, anneeScolaireId: config.anneeScolaireId });
            setNewLibelle(''); setCreating(false);
            await loadEvenements();
        } catch (err) { console.error('Erreur création:', err); }
    };

    // Lot 5.2 : la confirmation remonte au parent, qui porte la modale du module
    // (le picker est un popover de 230 px, il n'a pas la place d'en héberger une).
    const supprimerEvenement = async (evt) => {
        try {
            await evenementsAPI.deleteConfig(evt.id);
            await loadEvenements();
            if (window._reloadEvents) window._reloadEvents();
            if (window._reloadEventManager) window._reloadEventManager();
        } catch (err) { console.error('Erreur suppression:', err); }
    };

    const handleDeleteEvent = (e, evt) => {
        e.stopPropagation();
        onDemanderConfirmation({
            titre: 'Supprimer un événement',
            message: <>Supprimer définitivement l'événement <strong>{evt.libelle}</strong> ? Il sera retiré de la liste des événements types du département.</>,
            libelleConfirmer: 'Supprimer',
            danger: true,
            action: () => supprimerEvenement(evt)
        });
    };

    // Lot 5.4 bis : l'apparence se lit sur L'ÉVÉNEMENT, plus sur sa seule catégorie.
    // Sans ça, une icône ou une couleur choisie dans le référentiel se serait vue
    // partout SAUF ici — le popover aurait continué d'afficher le défaut de la
    // catégorie, et le réglage aurait paru sans effet.
    const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

    const nonPlaces = evenements.filter(e => !e.date_debut);
    const places = evenements.filter(e => e.date_debut);

    let posX = dateInfo.x;
    let posY = dateInfo.y;
    if (posX + 320 > window.innerWidth) posX = dateInfo.x - 330;
    if (posY + 400 > window.innerHeight) posY = window.innerHeight - 410;
    if (posY < 10) posY = 10;

    return (
        <div ref={pickerRef} className="date-event-picker" style={{ left: posX + 'px', top: posY + 'px' }}>
            <div className="dep-header">
                <div className="dep-title"><i className="fa-solid fa-calendar-day" /><strong>{formatDate(dateInfo.date)}</strong></div>
                {/* Lot 5.5 bis : c'était `.event-panel-close`, une classe du panneau
                    Événements qu'un second écran empruntait. Le panneau étant passé au
                    vocabulaire du module, cette croix prend `.pl-floatp-x` — le
                    remplacement est posé AVANT que la règle d'origine soit retirée. */}
                <button className="pl-floatp-x" onClick={onClose} aria-label="Fermer"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="dep-hint">Sélectionnez un événement à placer</div>
            {loading && <div className="loading"><div className="spinner"></div></div>}
            <div className="dep-body">
                {nonPlaces.length > 0 && (
                    <div className="dep-section">
                        <div className="dep-section-title"><i className="fa-solid fa-inbox" /> Disponibles</div>
                        {nonPlaces.map(evt => {
                            const couleur = couleurEvenement(evt, categories);
                            return (
                                <div key={evt.id} className="dep-event-item" onClick={() => onSelect(evt)} style={{ borderLeftColor: couleur }}>
                                    <span className="dep-event-icon" style={{ color: couleur }}><i className={'fa-solid ' + iconeEvenement(evt, categories)} /></span>
                                    <span className="dep-event-label">{evt.libelle}</span>
                                    <button className="dep-event-delete" onClick={(e) => handleDeleteEvent(e, evt)} title="Supprimer"><i className="fa-solid fa-trash" /></button>
                                </div>
                            );
                        })}
                    </div>
                )}
                {places.length > 0 && (
                    <div className="dep-section">
                        <div className="dep-section-title"><i className="fa-solid fa-location-dot" /> Déjà placés (déplacer)</div>
                        {places.map(evt => {
                            const couleur = couleurEvenement(evt, categories);
                            const oldDate = new Date(evt.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                            return (
                                <div key={evt.id} className="dep-event-item dep-event-placed" onClick={() => onSelect(evt)} style={{ borderLeftColor: couleur }}>
                                    <span className="dep-event-icon" style={{ color: couleur }}><i className={'fa-solid ' + iconeEvenement(evt, categories)} /></span>
                                    <span className="dep-event-label">{evt.libelle}</span>
                                    <span className="dep-event-old-date">{oldDate}</span>
                                    <button className="dep-event-delete" onClick={(e) => handleDeleteEvent(e, evt)} title="Supprimer"><i className="fa-solid fa-trash" /></button>
                                </div>
                            );
                        })}
                    </div>
                )}
                {evenements.length === 0 && !loading && (
                    <div className="dep-empty">Aucun événement. Créez-en un.</div>
                )}
                {creating ? (
                    <div className="dep-create-form">
                        <select value={newCategorie} onChange={(e) => setNewCategorie(e.target.value)} className="dep-create-select">
                            {CATEGORIES.map(cat => (<option key={cat.code} value={cat.code}>{cat.libelle}</option>))}
                        </select>
                        <input type="text" value={newLibelle} onChange={(e) => setNewLibelle(e.target.value)} placeholder="Nom..." className="dep-create-input" autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }} />
                        <button className="btn btn-success btn-small" onClick={handleCreate} disabled={!newLibelle.trim()} title="Créer"><i className="fa-solid fa-check" /></button>
                        <button className="btn btn-secondary btn-small" onClick={() => setCreating(false)} title="Annuler"><i className="fa-solid fa-xmark" /></button>
                    </div>
                ) : (
                    <button className="dep-create-btn" onClick={() => setCreating(true)}><i className="fa-solid fa-plus" /> Nouvel événement</button>
                )}
            </div>
        </div>
    );
}

export default PlanningApp;